import { useRef, useCallback } from 'react'
import { saveRawAudio } from '../engines/offline_storage'

/**
 * useDictation — Deepgram Live Streaming z warmup (eliminacja opóźnienia)
 *
 * Flow:
 *  1. warmup() → natychmiast po dotknięciu: mikrofon + WS + nagrywanie w closure
 *     audio buforowane od pierwszej chwili, requestData() co 150ms (iOS/Android fix)
 *  2. start()  → noop (warmup już uruchomił nagrywanie)
 *  3. cancel() → krótki tap: zamknij audio bez onDone
 *  4. stop()   → recorder.onstop → CloseStream → WS zamknięty w tle 6s → onDone
 *
 * Kluczowe właściwości:
 *  - WS lokalny per sesja w closure → stary WS dobiega w tle, nowa sesja startuje natychmiast
 *  - Session ID (sid) → sygnały/callbacks tylko dla aktywnej sesji
 *  - getUserMedia z wyłączonymi AGC/noise/echo → brak noise gate na starcie
 *  - UtteranceEnd → natychmiastowy promote interim na confirmed
 *  - interim → confirmed przy ws.onclose (ostatnia linia obrony)
 *  - Limit 45s → auto-stop
 */

const WS_PROXY_URL = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/api/speech/stream?industry=galvo`
const FLUSH_MS  = 150    // ms — interwał requestData() (iOS/Android flush)
const MAX_REC_MS = 45000 // ms — limit nagrywania

interface UseDictationOptions {
  onSegment:    (text: string, full: string) => void
  onSpeechFinal:(text: string, full: string) => void
  onDone:       (full: string) => void
  onError:      (msg: string) => void
  onMaxReached?: () => void  // opcjonalny callback przy auto-stop po 45s
}

export function useDictation({ onSegment, onSpeechFinal, onDone, onError, onMaxReached }: UseDictationOptions) {
  // Zasoby audio — globalne (jedna instancja na raz)
  const recorderRef   = useRef<MediaRecorder | null>(null)
  const streamRef     = useRef<MediaStream | null>(null)
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const maxTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Stan sesji
  const activeRef        = useRef<boolean>(false)
  const stopRequestedRef = useRef<boolean>(false)
  const cancelledRef     = useRef<boolean>(false)
  const currentSidRef    = useRef<number>(0)  // globalny licznik sesji
  const activeWsRef      = useRef<WebSocket | null>(null)  // WS bieżącej sesji (dla stop())

  // Transkrypcja
  const transcriptRef = useRef<string>('')
  const interimRef    = useRef<string>('')
  const audioChunksRef = useRef<Blob[]>([])

  // Stabilne referencje do callbacków
  const onSegmentRef     = useRef(onSegment)
  const onSpeechFinalRef = useRef(onSpeechFinal)
  const onDoneRef        = useRef(onDone)
  const onErrorRef       = useRef(onError)
  const onMaxReachedRef  = useRef(onMaxReached)
  onSegmentRef.current      = onSegment
  onSpeechFinalRef.current  = onSpeechFinal
  onDoneRef.current         = onDone
  onErrorRef.current        = onError
  onMaxReachedRef.current   = onMaxReached

  const warmup = useCallback(async () => {
    // Zwolnij zasoby audio poprzedniej sesji (WS zostaje — dobiega sam w tle)
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop() } catch (_) {}
    }
    recorderRef.current = null
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
    if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null }
    if (maxTimerRef.current)   { clearTimeout(maxTimerRef.current);    maxTimerRef.current   = null }

    // Lokalny WS dla tej sesji — żyje w closure, stary WS dobiega w tle
    let ws: WebSocket | null = null
    const sid = ++currentSidRef.current

    activeRef.current        = true
    stopRequestedRef.current = false
    cancelledRef.current     = false
    transcriptRef.current    = ''
    interimRef.current       = ''
    audioChunksRef.current   = []

    const t0 = performance.now()
    console.log(`[DG] warmup() sid=${sid}`)

    // ── 1. getUserMedia z wyłączonymi filtrami systemowymi ──
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl:  false,
          channelCount:     1,
        }
      })
      streamRef.current = stream
      console.log(`[DG] mic OK T=${(performance.now()-t0).toFixed(0)}ms`)
    } catch (e: any) {
      if (sid !== currentSidRef.current) return
      activeRef.current = false
      onErrorRef.current(`Mikrofon: ${e.message}`)
      return
    }

    if (cancelledRef.current || sid !== currentSidRef.current) {
      stream.getTracks().forEach(t => t.stop())
      streamRef.current = null
      activeRef.current = false
      return
    }

    // ── 2. MediaRecorder bez timeslice + requestData() co FLUSH_MS ──
    const ramBuf: Blob[] = []
    let wsReady = false

    const mimeType = getSupportedMime()
    let recorder: MediaRecorder
    try {
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    } catch (e: any) {
      activeRef.current = false
      stream.getTracks().forEach(t => t.stop()); streamRef.current = null
      onErrorRef.current(`MediaRecorder: ${e.message}`)
      return
    }
    recorderRef.current = recorder

    recorder.ondataavailable = (e) => {
      if (!e.data || e.data.size === 0 || cancelledRef.current) return
      audioChunksRef.current.push(e.data)
      if (wsReady && ws && ws.readyState === WebSocket.OPEN) {
        ws.send(e.data)
      } else {
        ramBuf.push(e.data)
      }
    }
    recorder.onerror = () => {}
    recorder.start()  // bez timeslice — kontrolujemy przez requestData()

    // Wymuś flush co FLUSH_MS (iOS/Android fix)
    flushTimerRef.current = setInterval(() => {
      if (recorder.state === 'recording') recorder.requestData()
    }, FLUSH_MS)

    console.log(`[DG] recorder started T=${(performance.now()-t0).toFixed(0)}ms mimeType=${recorder.mimeType}`)

    // ── 3. WebSocket — lokalny dla tej sesji ──
    try {
      ws = new WebSocket(WS_PROXY_URL)
    } catch (e: any) {
      if (sid !== currentSidRef.current) return
      activeRef.current = false
      if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null }
      recorder.stop(); stream.getTracks().forEach(t => t.stop()); streamRef.current = null
      onErrorRef.current(`WS init: ${e.message}`)
      return
    }

    const openTimeout = setTimeout(() => {
      if (ws && ws.readyState !== WebSocket.OPEN) {
        if (sid === currentSidRef.current) {
          activeRef.current = false
          if (!cancelledRef.current) onErrorRef.current('Timeout połączenia z Deepgram (8s)')
        }
        ws.close()
      }
    }, 8000)

    // ── 4. ws.onopen — flush ramBuf (nagłówek kontenera zawsze pierwszy) ──
    ws.onopen = () => {
      clearTimeout(openTimeout)
      activeWsRef.current = ws
      console.log(`[DG] WS open T=${(performance.now()-t0).toFixed(0)}ms buf=${ramBuf.length} sid=${sid}`)
      if (cancelledRef.current || stopRequestedRef.current) { ws!.close(1000); return }
      ramBuf.forEach(blob => ws!.send(blob))
      ramBuf.length = 0
      wsReady = true
    }

    // ── 5. onmessage — tylko dla bieżącej sesji ──
    ws.onmessage = (ev) => {
      if (sid !== currentSidRef.current || cancelledRef.current) return
      let msg: any
      try { msg = JSON.parse(ev.data) } catch { return }

      if (msg.type === 'Results') {
        const text = (msg.channel?.alternatives?.[0]?.transcript ?? '').trim()
        if (!text) return

        if (msg.is_final) {
          transcriptRef.current = transcriptRef.current ? transcriptRef.current + ' ' + text : text
          interimRef.current = ''
        } else {
          interimRef.current = text
        }

        const full = interimRef.current
          ? (transcriptRef.current ? transcriptRef.current + ' ' + interimRef.current : interimRef.current)
          : transcriptRef.current

        onSegmentRef.current(text, full)

        if (msg.speech_final && transcriptRef.current) {
          onSpeechFinalRef.current(text, transcriptRef.current)
        }
      } else if (msg.type === 'UtteranceEnd') {
        // Deepgram wykrył ciszę — jeśli zostaje interim, promuj na confirmed natychmiast
        if (interimRef.current) {
          transcriptRef.current = transcriptRef.current
            ? transcriptRef.current + ' ' + interimRef.current
            : interimRef.current
          interimRef.current = ''
          const full = transcriptRef.current
          onSegmentRef.current('', full)
          onSpeechFinalRef.current('', full)
        }
      }
    }

    ws.onerror = () => {
      clearTimeout(openTimeout)
      if (sid === currentSidRef.current && !cancelledRef.current) {
        onErrorRef.current('Błąd połączenia z Deepgram')
      }
    }

    ws.onclose = () => {
      clearTimeout(openTimeout)
      if (activeWsRef.current === ws) activeWsRef.current = null

      // Tylko bieżąca sesja aktualizuje stan i wywołuje callbacki
      if (sid !== currentSidRef.current) return

      activeRef.current = false
      // Ostatnia linia obrony — interim który nie dostał is_final
      if (interimRef.current) {
        transcriptRef.current = transcriptRef.current
          ? transcriptRef.current + ' ' + interimRef.current
          : interimRef.current
        interimRef.current = ''
      }

      const final = transcriptRef.current.trim()
      transcriptRef.current = ''
      interimRef.current    = ''

      if (cancelledRef.current) {
        console.log(`[DG] sid=${sid} cancelled, skipping onDone`)
        return
      }
      if (stopRequestedRef.current || final) {
        console.log(`[DG] sid=${sid} calling onDone with:`, final.slice(0, 50))
        onDoneRef.current(final)
      } else {
        console.log(`[DG] sid=${sid} no final text and not stopped, skipping onDone`)
      }
    }
  }, [])

  const start = useCallback(async () => {
    if (!activeRef.current) return warmup()
    // Warmup już aktywny — ustaw limit 45s
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current)
    maxTimerRef.current = setTimeout(() => {
      onMaxReachedRef.current?.()
      stop()
    }, MAX_REC_MS)
  }, [warmup])

  const cancel = useCallback(() => {
    cancelledRef.current     = true
    activeRef.current        = false
    stopRequestedRef.current = false

    if (maxTimerRef.current)   { clearTimeout(maxTimerRef.current);    maxTimerRef.current   = null }
    if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop() } catch (_) {}
    }
    recorderRef.current = null
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
    // WS lokalny w closure — nie mamy do niego dostępu stąd, zamknie się sam przez onclose
    transcriptRef.current = ''
    interimRef.current    = ''
  }, [])

  const stop = useCallback(() => {
    if (cancelledRef.current || !activeRef.current) return
    stopRequestedRef.current = true
    activeRef.current        = false

    if (maxTimerRef.current)   { clearTimeout(maxTimerRef.current);    maxTimerRef.current   = null }
    if (flushTimerRef.current) { clearInterval(flushTimerRef.current); flushTimerRef.current = null }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }

    // CloseStream przez recorder.onstop — gwarantuje że ostatni chunk już wyszedł
    const wsRef = activeWsRef.current
    const sendClose = () => {
      if (wsRef && wsRef.readyState === WebSocket.OPEN) {
        wsRef.send(JSON.stringify({ type: 'CloseStream' }))
      }
      // WS zamknie się sam po 6s w tle — nie blokuje UI
      setTimeout(() => {
        if (wsRef && wsRef.readyState !== WebSocket.CLOSED) wsRef.close(1000)
      }, 6000)
    }

    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      const rec = recorderRef.current
      rec.onstop = () => {
        if (!cancelledRef.current) {
          persistAudio(audioChunksRef.current, rec.mimeType || '').catch(() => {})
        }
        sendClose()
      }
      rec.stop()
    } else {
      sendClose()
    }
    recorderRef.current = null
  }, [])

  return { warmup, start, stop, cancel }
}

function getSupportedMime(): string | null {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
  ]
  return types.find(t => MediaRecorder.isTypeSupported(t)) ?? null
}

async function persistAudio(chunks: Blob[], mimeType: string) {
  if (!chunks.length) return
  try {
    const blob = new Blob(chunks, { type: mimeType || 'audio/webm' })
    const buf = await blob.arrayBuffer()
    const digest = await crypto.subtle.digest('SHA-256', buf)
    const hash = Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('')
    await saveRawAudio(hash, blob, Date.now())
    console.log('[DG] raw audio saved', hash, blob.size)
  } catch (e: any) {
    console.warn('[DG] persistAudio failed', e.message)
  }
}
